import { useTranslation } from "gatsby-plugin-react-i18next";
import React, { useState, useEffect } from "react";
import AnchorLink from "react-anchor-link-smooth-scroll";
import Icon from "@atoms/Icon/Icon";
import Button from "@atoms/Button/Button";
import Logo from "@atoms/Logo/Logo";
import LanguageSwitcher from "@components/translation";
import { useLocale } from "@hooks";
import NavDropdown from "../NavDropdown/NavDropdown";
import "./HeaderNav.scss";
import HeaderNavChapter from "./HeaderNavChapter";
import HeaderNavSubMenu from "./HeaderNavSubMenu";
import type { SubMenu } from "./HeaderNav";
import { IoIosClose, IoIosMenu } from "react-icons/io";
import Link from "@components/Link";
import { IconShare } from "@atoms/Icon/list";
import { useTracking } from "../../hooks";
import { AnalyticsEventType } from "../../context/TrackingProvider";

interface Props {
	theme: "white" | "dark";
	signUpAsProviderShown?: boolean;
	firstSubMenu: SubMenu[];
	secondSubMenu: SubMenu[];
	headerIsFixed: boolean;
}

const ABOUT_DROPDOWNS = [
	{
		label: "nav.label.approach",
		to: "/approach/",
	},
	{
		label: "nav.label.stories",
		to: "/inspiration/",
	},
	{
		label: "nav.label.coverage",
		to: "/coverage/",
	},
	{
		label: "nav.label.all.psychologists",
		to: "/providers/",
	},
	{
		label: "nav.label.about.us",
		to: "/about/",
	},
];

const HeaderNavDesktop: React.FC<Props> = ({
	theme,
	signUpAsProviderShown,
	firstSubMenu,
	secondSubMenu,
	headerIsFixed,
}) => {
	const { t } = useTranslation();
	const { getAppLinkWithCurrentLang, currentLanguage } = useLocale();
	const { track } = useTracking();

	const [headerSubMenuShown, setHeaderSubMenuShown] = useState(false);

	const toggleMenu = () => {
		setHeaderSubMenuShown(!headerSubMenuShown);
	};

	useEffect(() => {
		headerSubMenuShown
			? (document.documentElement.style.overflow = "hidden")
			: (document.documentElement.style.overflow = "auto");
	}, [headerSubMenuShown]);

	return (
		<>
			<div
				className={`HeaderNavV2 g_hide_from_tablet_portrait theme-${theme} ${
					headerIsFixed ? "is-fixed" : ""
				} ${headerSubMenuShown ? "is-sub-menu-shown" : ""}`}
				onMouseLeave={() => setHeaderSubMenuShown(false)}
			>
				<nav className="HeaderNav--inner">
					<div className="HeaderNav--item--left">
						<div className="HeaderNav--item--logo">
							<Logo theme={theme} />
						</div>
						{!headerSubMenuShown && <HeaderNavChapter />}
					</div>
					<div className="HeaderNav--item--right">
						{!headerSubMenuShown && (
							<NavDropdown
								className="HeaderNav--item--link HeaderNav--secondPriorityItem"
								label={t("nav.label.about")}
								baseLink={"/approach/"}
								list={ABOUT_DROPDOWNS}
							/>
						)}

						{!headerSubMenuShown && (
							<Link
								to="/for-therapists/"
								className="HeaderNav--item--link"
								activeClassName="is-active"
							>
								{t("nav.label.join.as.psychologist")}
							</Link>
						)}

						<a
							href={`${process.env.GATSBY_AEPSY_B2B_URL}/${currentLanguage}`}
							target="_blank"
							rel="noreferrer"
							className="HeaderNav--item--link HeaderNav--external-link-with-icon"
							onClick={() => {
								track({
									eventType: AnalyticsEventType.GButtonClick,
									data: {
										buttonName: "header_navigation_desktop",
										sourceUrl: window.location.pathname,
										targetUrl: `${process.env.GATSBY_AEPSY_B2B_URL}/${currentLanguage}`,
									},
								});
							}}
						>
							{t("nav.label.for.partners")}
							<Icon
								icon={<IconShare />}
								variant="outlined"
								theme="white"
								size="xxs"
							/>
						</a>

						<a
							className="HeaderNav--item--link"
							href={getAppLinkWithCurrentLang("https://app.aepsy.com/login")}
						>
							<Button
								label={t("nav.label.login")}
								variant="outlined"
								theme={headerIsFixed || headerSubMenuShown ? "white" : theme}
								size="xxs"
								radius="xxs"
							/>
						</a>

						<div className="HeaderNav--LanguageSwitcher">
							<LanguageSwitcher
								id="LanguageSwitcherHeader"
								theme={headerIsFixed || headerSubMenuShown ? "white" : theme}
								horizontal="right"
								size="xxs"
							/>
						</div>

						{headerIsFixed && (
							signUpAsProviderShown ? (
								<AnchorLink
									offset={() => 120}
									href="#provider-signup"
									className="HeaderNav--item--link HeaderNav--cta-desktop"
								>
									<Button
										label={t("join.as.psychologist.cta.label")}
										size="s"
										theme="pink-gradient"
									/>
								</AnchorLink>
							) : (
								<a
									href={getAppLinkWithCurrentLang(
										process.env.GATSBY_START_FULL_FUNNEL_URL,
									)}
									className="HeaderNav--item--link HeaderNav--cta-desktop"
								>
									<Button
										label={t("button.get.started")}
										size="s"
										theme="pink-gradient"
									/>
								</a>
							)
						)}

						<Button
							onClick={toggleMenu}
							label={headerSubMenuShown ? <IoIosClose /> : <IoIosMenu />}
							theme={headerIsFixed || headerSubMenuShown ? "white" : theme}
							variant="inline"
							classes="HeaderNav--burgerMenu"
						/>
					</div>
				</nav>
				<HeaderNavSubMenu
					firstSubMenu={firstSubMenu}
					secondSubMenu={secondSubMenu}
					headerSubMenuShown={headerSubMenuShown}
				/>
			</div>
		</>
	);
};

export default HeaderNavDesktop;
